import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Badge from "@mui/material/Badge";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "react-select";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { toPartDateInput } from "../Parts/partFormConfig";
import { formatTireSizeInput } from "../Tires/tireFormConfig";

const TAB_LABELS = {
  services: "Services",
  parts: "Parts",
  tires: "Tires",
  oil: "Oil Change",
};

function ServicesPartsInlineTables({
  TOKEN,
  tab: controlledTab,
  onTabChange,
  editingKey: controlledEditingKey,
  onEditingKeyChange,
  // Services
  selectedServices = [],
  serviceCatalogOptions = [],
  onAddCatalogService,
  onAddBlankService,
  onUpdateLine,
  onRemoveLine,
  techniciansOptions = [],
  // Parts
  partsCatalogOptions = [],
  partsCatalogLoading = false,
  nonInventoryPartsCatalogOptions = [],
  nonInventoryPartsCatalogLoading = false,
  onSearchParts,
  formatPartOptionLabel,
  onAddCatalogPart,
  onAddBlankPart,
  vendorOptions = [],
  errors = {},
  // Tires
  tiresCatalogOptions = [],
  selectedTiresList = [],
  onAddCatalogTire,
  onAddBlankTire,
  onUpdateTireLine,
  onRemoveTireLine,
  // Oil Change
  oilsOptions = [],
  filtersOptions = [],
  extraOilOptions = [],
  selectedOil,
  selectedExtraOil,
  selectedOilFilter,
  oilTechnician = [],
  receiptOil = {},
  FULL_SERVICE_PRICE = 0,
  onSelectOil,
  onOilPriceOverride,
  onSelectExtraOil,
  onExtraOilQtyChange,
  onExtraOilUnitPriceChange,
  onSelectOilFilter,
  onOilFilterPriceOverride,
  onToggleFullService,
  onSelectOilTechnician,
  oilChangeAdded = false,
  onAddOilChangeRow,
  onRemoveOilChange,
}) {
  const [internalTab, setInternalTab] = useState("services");
  const [internalEditingKey, setInternalEditingKey] = useState(null);

  const isTabControlled =
    controlledTab !== undefined && typeof onTabChange === "function";
  const isEditingKeyControlled =
    controlledEditingKey !== undefined &&
    typeof onEditingKeyChange === "function";

  const tab = isTabControlled ? controlledTab : internalTab;
  const setTab = isTabControlled ? onTabChange : setInternalTab;
  const editingKey = isEditingKeyControlled
    ? controlledEditingKey
    : internalEditingKey;
  const setEditingKey = isEditingKeyControlled
    ? onEditingKeyChange
    : setInternalEditingKey;

  const [pendingTab, setPendingTab] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [inlineValidationErrors, setInlineValidationErrors] = useState({});

  const serviceRows = useMemo(
    () => selectedServices.filter((row) => row?.value?.type !== "part"),
    [selectedServices],
  );
  const partRows = useMemo(
    () => selectedServices.filter((row) => row?.value?.type === "part"),
    [selectedServices],
  );
  const tireRows = selectedTiresList || [];

  // Preserve the old Normal/Group/Custom service discovery in the new table UI.
  const groupedServiceOptions = useMemo(() => {
    const normal = [];
    const group = [];
    const custom = [];

    (serviceCatalogOptions || []).forEach((option) => {
      if (!option?.value) return;
      if (option.value.isCustom || option.value.isCustomTemplate) {
        custom.push(option);
      } else if (option.value.serviceType === "Group") {
        group.push(option);
      } else {
        normal.push(option);
      }
    });

    return [
      normal.length ? { label: "Normal Services", options: normal } : null,
      group.length ? { label: "Group Services", options: group } : null,
      custom.length ? { label: "Custom", options: custom } : null,
    ].filter(Boolean);
  }, [serviceCatalogOptions]);

  const formatServiceCatalogOption = (option, { context }) => {
    if (context === "value") return option?.label || "Service";

    const value = option?.value || {};
    const isGroup = value.serviceType === "Group";
    const isCustom = value.isCustom || value.isCustomTemplate;
    const typeLabel = isCustom
      ? "Custom"
      : isGroup
        ? "Group Service"
        : "Normal Service";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {option?.label || value.name || "Service"}
          </Typography>
          {value.description && (
            <Typography
              component="div"
              sx={{
                fontSize: 11,
                color: "#6B7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value.description}
            </Typography>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            px: 0.75,
            py: 0.2,
            borderRadius: 1,
            fontSize: 10.5,
            fontWeight: 700,
            color: isGroup ? "#6D28D9" : isCustom ? "#475569" : "#166534",
            backgroundColor: isGroup
              ? "#EDE9FE"
              : isCustom
                ? "#E2E8F0"
                : "#DCFCE7",
          }}
        >
          {typeLabel}
        </Box>
      </Box>
    );
  };

  const formatTireCatalogOption = (option, { context }) => {
    if (context === "value") return option.label;

    const location = option.value?.location?.name || "No location";
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{option.label}</Box>
        <Box
          component="span"
          title={`Location: ${location}`}
          sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, maxWidth: "45%", fontSize: 12, opacity: 0.8 }}
        >
          <LocationOnOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          <Box component="span" sx={{ textAlign: "right", overflowWrap: "anywhere" }}>{location}</Box>
        </Box>
      </Box>
    );
  };

  const rowKey = (row) => row?.value?.lineItemId;

  /*
   * Existing hydrated rows must never auto-open.
   * New rows are opened explicitly by the add handlers below.
   */

  const hasActiveEdit = Boolean(editingKey);
  const hasActiveServiceEdit = serviceRows.some(
    (row) => rowKey(row) === editingKey,
  );
  const hasActivePartEdit = partRows.some((row) => rowKey(row) === editingKey);
  const serviceAddDisabled = hasActiveEdit && !hasActiveServiceEdit;
  const partsAddDisabled = hasActiveEdit && !hasActivePartEdit;

  const getEditingRowLabel = () => {
    if (editingKey === "oil-change") {
      return "Oil Change";
    }

    if (editingKey?.startsWith("tire-")) {
      return "Tire";
    }

    const row = [...serviceRows, ...partRows].find(
      (r) => rowKey(r) === editingKey,
    );

    if (row) {
      return row?.value?.type === "part" ? "Part" : "Service";
    }

    return "row";
  };
  const canStartAnotherEdit = () => {
    if (!hasActiveEdit) {
      return true;
    }

    return false;
  };
  const openAddedRow = (nextTab, addedRow) => {
    const key =
      addedRow?.value?.lineItemId ??
      addedRow?.value?.id ??
      addedRow?.id ??
      addedRow?.value?._id;

    if (!key) return false;

    setTab(nextTab);
    setEditingKey(key);
    return true;
  };

  const handleAddCatalogService = (option) => {
    if (!option || serviceAddDisabled) return;

    const addedRow = onAddCatalogService(option);
    openAddedRow("services", addedRow);
  };

  const handleAddBlankService = () => {
    if (serviceAddDisabled) return;

    const addedRow = onAddBlankService();
    openAddedRow("services", addedRow);
  };

  const handleAddBlankGroupService = () => {
    if (serviceAddDisabled) return;

    const addedRow = onAddBlankService({ serviceType: "Group" });
    openAddedRow("services", addedRow);
  };

  const handleAddCatalogPart = (option) => {
    if (!option || partsAddDisabled) return;

    const addedRow = onAddCatalogPart(option);
    openAddedRow("parts", addedRow);
  };

  const handleAddBlankPart = () => {
    if (partsAddDisabled) return;

    const addedRow = onAddBlankPart();
    openAddedRow("parts", addedRow);
  };

  const handleAddCatalogTire = (option) => {
    if (!option || !canStartAnotherEdit()) return;

    const addedRow = onAddCatalogTire(option);
    const id = addedRow?.id ?? addedRow?.value?.id ?? addedRow?.value?._id;
    if (id) {
      setTab("tires");
      setEditingKey(`tire-${id}`);
    }
  };

  const handleAddBlankTire = () => {
    if (!canStartAnotherEdit()) return;

    const addedRow = onAddBlankTire();
    const id = addedRow?.id ?? addedRow?.value?.id ?? addedRow?.value?._id;
    if (id) {
      setTab("tires");
      setEditingKey(`tire-${id}`);
    }
  };

  const handleAddOilChange = () => {
    if (!canStartAnotherEdit()) return;

    const added = onAddOilChangeRow();
    // Oil change has one fixed row, so there is no generated row id.
    // The successful add action itself is the explicit edit trigger.
    if (added !== false) {
      setTab("oil");
      setEditingKey("oil-change");
    }
  };
  const handleRemoveLine = (key) => {
    onRemoveLine(key);
    setInlineValidationErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (editingKey === key) {
      setEditingKey(null);
    }
  };
  const handleRemoveTireLine = (id) => {
    onRemoveTireLine(id);

    if (editingKey === `tire-${id}`) {
      setEditingKey(null);
    }
  };

  const handleRemoveOilChange = () => {
    if (onRemoveOilChange) {
      onRemoveOilChange();
    }

    if (editingKey === "oil-change") {
      setEditingKey(null);
    }
  };
  const handleDoneEditing = (key = null) => {
    if (key && editingKey !== key) return;

    const activeKey = key || editingKey;
    if (!activeKey) return;

    const row = [...serviceRows, ...partRows].find(
      (r) => rowKey(r) === activeKey,
    );

    if (!row) {
      setEditingKey(null);
      return;
    }

    const value = row.value || {};
    const nextErrors = {};
    const isPart = value.type === "part";
    const name = String(value.name || "").trim();
    const quantity = Number(value.quantity);

    // Standalone receipt parts do not require a technician.
    if (!name) {
      nextErrors.serviceName = isPart
        ? "Enter part name"
        : "Enter service name";
    }
    if (!Number.isFinite(quantity) || quantity < 1) {
      nextErrors.serviceQuantity = "Enter quantity 1 or more";
    }

    if (!isPart) {
      const technicians = Array.isArray(value.technicians)
        ? value.technicians
        : value.technician
          ? [value.technician]
          : [];
      if (technicians.length === 0) {
        nextErrors.technician = "Select technician";
      }
    }

    if (Object.keys(nextErrors).length) {
      setInlineValidationErrors((prev) => ({
        ...prev,
        [activeKey]: nextErrors,
      }));
      return;
    }

    setInlineValidationErrors((prev) => {
      const next = { ...prev };
      delete next[activeKey];
      return next;
    });
    setEditingKey(null);
  };

  /**
   * Prevent editing another row while one row is already active.
   */
  const activateRow = (key) => {
    if (!key) return;

    if (editingKey && editingKey !== key) {
      const nextIsService = serviceRows.some((row) => rowKey(row) === key);
      const nextIsPart = partRows.some((row) => rowKey(row) === key);
      if (
        (!hasActiveServiceEdit || !nextIsService) &&
        (!hasActivePartEdit || !nextIsPart)
      ) {
        return;
      }
    }

    setInlineValidationErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setEditingKey(key);
  };
  const isEditingRowIncomplete = () => {
    if (!editingKey) return false;
    if (editingKey === "oil-change") {
      return (
        !receiptOil.oil &&
        !receiptOil.oilFilter &&
        !receiptOil.extraOilSelection &&
        !receiptOil.fullServiceIncluded
      );
    }
    if (editingKey.startsWith("tire-")) {
      const tireId = editingKey.replace("tire-", "");
      const tire = tireRows.find((t) => String(t.id) === String(tireId));
      return tire ? !tire.brand && !tire.size : false;
    }
    const row = [...serviceRows, ...partRows].find(
      (r) => rowKey(r) === editingKey,
    );
    return row ? !row.value?.name : false;
  };

  const requestTabChange = (nextTab) => {
    if (nextTab === tab) return;
    if (isEditingRowIncomplete()) {
      setPendingTab(nextTab);
      setShowDiscardConfirm(true);
      return;
    }
    setTab(nextTab);
    setEditingKey(null);
  };

  const handleTabChange = (_e, v) => requestTabChange(v);

  const handleConfirmDiscard = () => {
    if (editingKey?.startsWith("tire-")) {
      onRemoveTireLine(editingKey.replace("tire-", ""));
    } else if (editingKey === "oil-change") {
      onRemoveOilChange && onRemoveOilChange();
    } else if (editingKey) {
      onRemoveLine(editingKey);
    }
    setEditingKey(null);
    setTab(pendingTab);
    setPendingTab(null);
    setShowDiscardConfirm(false);
  };

  const handleCancelDiscard = () => {
    setPendingTab(null);
    setShowDiscardConfirm(false);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          mb: 1.5,
          borderBottom: "1px solid #E5E7EB",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            minHeight: 36,
            gap: 0.5,
          },
        }}
      >
        <Tab
          value="services"
          label={<TabLabel text="Services" count={serviceRows.length} />}
        />
        <Tab
          value="parts"
          label={<TabLabel text="Parts" count={partRows.length} />}
        />
        <Tab
          value="tires"
          label={<TabLabel text="Tires" count={tireRows.length} />}
        />
        <Tab
          value="oil"
          label={<TabLabel text="Oil Change" count={oilChangeAdded ? 1 : 0} />}
        />
      </Tabs>

      {tab === "services" && (
        <Box>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 280, flex: 1 }}>
              <Select
                options={groupedServiceOptions}
                value={null}
                onChange={handleAddCatalogService}
                isDisabled={serviceAddDisabled}
                formatOptionLabel={formatServiceCatalogOption}
                placeholder={
                  serviceAddDisabled
                    ? `Finish editing ${getEditingRowLabel()} first`
                    : "Add a service from catalog…"
                }
                isClearable
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                styles={{
                  ...inlineSelectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
                  control: (base, state) => ({
                    ...inlineSelectStyles.control(base, state),
                    minHeight: 38,
                  }),
                }}
              />
            </Box>
          </Box>

          <ScrollTableBox>
            <Table size="small" sx={{ minWidth: 850 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 190 }}>Service</TableCell>
                  <TableCell sx={{ minWidth: 210 }}>Description</TableCell>
                  <TableCell sx={{ minWidth: 190 }}>Technician(s)</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>Price</TableCell>
                  <TableCell align="right" sx={{ width: 70 }}>Qty</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>Total</TableCell>
                  <TableCell align="right" sx={{ width: 96 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceRows.length === 0 ? (
                  <EmptyRow colSpan={7} label="No services added yet" />
                ) : (
                  serviceRows.map((row) => (
                    <ServiceRow
                      key={rowKey(row)}
                      row={row}
                      isEditing={editingKey === rowKey(row)}
                      onActivate={() => activateRow(rowKey(row))}
                      onDone={() => handleDoneEditing(rowKey(row))}
                      onUpdateLine={onUpdateLine}
                      onRemoveLine={handleRemoveLine}
                      techniciansOptions={techniciansOptions}
                      validationErrors={
                        inlineValidationErrors[rowKey(row)] || {}
                      }
                      TOKEN={TOKEN}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollTableBox>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <AddEntryButton
              TOKEN={TOKEN}
              onAdd={handleAddBlankService}
              label="Add Service"
              disabled={serviceAddDisabled}
            />
            <AddEntryButton
              TOKEN={TOKEN}
              onAdd={handleAddBlankGroupService}
              label="Add Group Service"
              disabled={serviceAddDisabled}
            />
          </Box>
          {errors.technician && (
            <Typography
              variant="caption"
              sx={{ color: "#DC2626", display: "block", mt: 0.5 }}
            >
              {errors.technician}
            </Typography>
          )}
        </Box>
      )}

      {tab === "parts" && (
        <Box>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            {[
              {
                label: "Inventory Parts",
                options: partsCatalogOptions,
                loading: partsCatalogLoading,
                onSearch: onSearchParts,
              },
              {
                label: "Non-Inventory Parts",
                options: nonInventoryPartsCatalogOptions,
                loading: nonInventoryPartsCatalogLoading,
              },
            ].map(({ label, options, loading, onSearch }) => (
              <Box key={label} sx={{ minWidth: 260, flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                  {label}
                </Typography>
                <Select
                  aria-label={label}
                  options={options}
                  value={null}
                  onChange={handleAddCatalogPart}
                  onInputChange={(inputValue, actionMeta) => {
                    if (actionMeta?.action === "input-change") {
                      onSearch?.(inputValue);
                    }
                    return inputValue;
                  }}
                  isLoading={loading}
                  filterOption={({ data }, inputValue) =>
                    [data.label, data.value?.description, data.value?.vendor]
                      .filter(Boolean)
                      .join(" ")
                      .toLowerCase()
                      .includes(inputValue.trim().toLowerCase())
                  }
                  isDisabled={partsAddDisabled}
                  formatOptionLabel={formatPartOptionLabel}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? `No matching ${label.toLowerCase()} found`
                      : `No available ${label.toLowerCase()}`
                  }
                  placeholder={
                    partsAddDisabled
                      ? `Finish editing ${getEditingRowLabel()} first`
                      : `Add from ${label.toLowerCase()}…`
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: 34,
                      fontSize: 13,
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1600 }),
                  }}
                />
              </Box>
            ))}
          </Box>

          <ScrollTableBox>
            <Table size="small" sx={partSpreadsheetTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>Part</TableCell>
                  {PART_COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      sx={{ width: col.width }}
                    >
                      {col.header}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: 90 }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ width: 80 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partRows.length === 0 ? (
                  <EmptyRow
                    colSpan={PART_COLUMNS.length + 3}
                    label="No parts added yet"
                  />
                ) : (
                  partRows.map((row) => (
                    <PartRow
                      key={rowKey(row)}
                      row={row}
                      isEditing={editingKey === rowKey(row)}
                      onActivate={() => activateRow(rowKey(row))}
                      onDone={() => handleDoneEditing(rowKey(row))}
                      onUpdateLine={onUpdateLine}
                      onRemoveLine={handleRemoveLine}
                      vendorOptions={vendorOptions}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollTableBox>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <AddEntryButton
              TOKEN={TOKEN}
              onAdd={handleAddBlankPart}
              label="Add Part"
              disabled={partsAddDisabled}
            />
          </Box>
        </Box>
      )}

      {tab === "tires" && (
        <Box>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 260, flex: 1 }}>
              <Select
                options={tiresCatalogOptions}
                formatOptionLabel={formatTireCatalogOption}
                value={null}
                onChange={handleAddCatalogTire}
                isDisabled={hasActiveEdit}
                placeholder={
                  hasActiveEdit
                    ? `Finish editing ${getEditingRowLabel()} first`
                    : "Add a tire from inventory…"
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 34,
                    fontSize: 13,
                  }),
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {tireRows.length === 0 ? (
              <Box sx={tireGridSx}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: "center" }}
                >
                  No tires added yet
                </Typography>
              </Box>
            ) : (
              tireRows.map((tire) => (
                <TireRow
                  key={tire.id}
                  tire={tire}
                  isEditing={editingKey === `tire-${tire.id}`}
                  onActivate={() => activateRow(`tire-${tire.id}`)}
                  onDone={() => handleDoneEditing(`tire-${tire.id}`)}
                  onUpdateTireLine={onUpdateTireLine}
                  onRemoveTireLine={handleRemoveTireLine}
                  techniciansOptions={techniciansOptions}
                  TOKEN={TOKEN}
                />
              ))
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <AddEntryButton
              TOKEN={TOKEN}
              onAdd={handleAddBlankTire}
              label="New tire"
              disabled={hasActiveEdit}
            />
          </Box>
          {errors.tireTechnician && (
            <Typography
              variant="caption"
              sx={{ color: "#DC2626", display: "block", mt: 0.5 }}
            >
              {errors.tireTechnician}
            </Typography>
          )}
        </Box>
      )}

      {tab === "oil" && (
        <Box>
          <OilChangeTable
            TOKEN={TOKEN}
            oilChangeAdded={oilChangeAdded}
            isEditing={editingKey === "oil-change"}
            onActivate={() => activateRow("oil-change")}
            onDone={() => handleDoneEditing("oil-change")}
            onAddOilChangeRow={handleAddOilChange}
            onRemoveOilChange={handleRemoveOilChange}
            oilsOptions={oilsOptions}
            filtersOptions={filtersOptions}
            extraOilOptions={extraOilOptions}
            techniciansOptions={techniciansOptions}
            selectedOil={selectedOil}
            selectedExtraOil={selectedExtraOil}
            selectedOilFilter={selectedOilFilter}
            oilTechnician={oilTechnician}
            receiptOil={receiptOil}
            FULL_SERVICE_PRICE={FULL_SERVICE_PRICE}
            onSelectOil={onSelectOil}
            onOilPriceOverride={onOilPriceOverride}
            onSelectExtraOil={onSelectExtraOil}
            onExtraOilQtyChange={onExtraOilQtyChange}
            onExtraOilUnitPriceChange={onExtraOilUnitPriceChange}
            onSelectOilFilter={onSelectOilFilter}
            onOilFilterPriceOverride={onOilFilterPriceOverride}
            onToggleFullService={onToggleFullService}
            onSelectOilTechnician={onSelectOilTechnician}
          />
        </Box>
      )}

      {/* ── Tab-switch guard dialog ── */}
      <Dialog
        open={showDiscardConfirm}
        onClose={handleCancelDiscard}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Discard unfinished{" "}
          {editingKey?.startsWith("tire-")
            ? "tire"
            : editingKey === "oil-change"
              ? "oil change"
              : "row"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You started adding a{" "}
            {editingKey?.startsWith("tire-") ? "tire" : "row"} in{" "}
            {TAB_LABELS[tab]} but haven't finished it yet. Switching to{" "}
            {pendingTab ? TAB_LABELS[pendingTab] : "another tab"} will discard
            it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDiscard} variant="outlined">
            No, stay here
          </Button>
          <Button
            onClick={handleConfirmDiscard}
            variant="contained"
            color="error"
          >
            Yes, discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function TabLabel({ text, count }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <span>{text}</span>
      <Badge
        badgeContent={count}
        color="primary"
        sx={{
          "& .MuiBadge-badge": { position: "static", transform: "none" },
        }}
      />
    </Box>
  );
}

function ScrollTableBox({ children }) {
  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 1,
        overflowX: "auto",
        overflowY: "hidden",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      {children}
    </Box>
  );
}

function AddOilChangeRow({ TOKEN, onAdd }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onAdd}
      sx={{
        width: "100%",
        py: 1.5,
        border: `1.5px dashed ${TOKEN.primary}`,
        borderRadius: 1,
        background: "#fff",
        color: TOKEN.primary,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        "&:hover": { background: `${TOKEN.primary}0A` },
      }}
    >
      <AddIcon sx={{ fontSize: 16 }} /> Add oil change
    </Box>
  );
}

function OilGridCell({ label, children, sx }) {
  return (
    <Box className="oil-grid-cell" sx={{ ...oilGridCellSx, ...sx }}>
      {label ? <Typography sx={oilFieldLabelSx}>{label}</Typography> : null}
      {children}
    </Box>
  );
}

function AddEntryButton({ TOKEN, onAdd, label, disabled = false }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={disabled ? undefined : onAdd}
      disabled={disabled}
      sx={{
        flex: 1,
        py: 1.25,
        border: `1.5px dashed ${disabled ? "#CBD5E1" : TOKEN.primary}`,
        borderRadius: 1,
        background: disabled ? "#F8FAFC" : "#fff",
        color: disabled ? "#94A3B8" : TOKEN.primary,
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        opacity: disabled ? 0.7 : 1,
        "&:hover": {
          background: disabled ? "#F8FAFC" : `${TOKEN.primary}0A`,
        },
      }}
    >
      <AddIcon sx={{ fontSize: 16 }} /> {label}
    </Box>
  );
}
function EmptyRow({ colSpan, label }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 2, textAlign: "center" }}
        >
          {label}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/* ─── Shared compact styles for inline cell editing ─── */
const INLINE_CONTROL_HEIGHT = 34;

const cellTextFieldSx = {
  "& .MuiInputBase-root": {
    height: INLINE_CONTROL_HEIGHT,
    fontSize: 13,
    alignItems: "center",
  },
  "& .MuiInputBase-input": {
    height: INLINE_CONTROL_HEIGHT,
    boxSizing: "border-box",
    fontSize: 13,
    padding: "0 8px",
  },
};

const inlineSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: INLINE_CONTROL_HEIGHT,
    height: INLINE_CONTROL_HEIGHT,
    fontSize: 12.5,
    borderColor: state.isFocused ? "#94a3b8" : base.borderColor,
  }),
  valueContainer: (base) => ({
    ...base,
    height: INLINE_CONTROL_HEIGHT,
    padding: "0 6px",
    overflow: "hidden",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({
    ...base,
    height: INLINE_CONTROL_HEIGHT,
  }),
  dropdownIndicator: (base) => ({ ...base, padding: 6 }),
  clearIndicator: (base) => ({ ...base, padding: 6 }),
  multiValue: (base) => ({ ...base, margin: "1px 2px" }),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
};

const wideInlineSelectMenuStyles = {
  ...inlineSelectStyles,
  menu: (base) => ({
    ...base,
    width: 380,
    minWidth: "100%",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 320,
  }),
  option: (base) => ({
    ...base,
    fontSize: 14,
    lineHeight: 1.35,
    padding: "10px 12px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
};

const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

const oilCompactNumberTextFieldSx = {
  ...cellTextFieldSx,
  width: 82,
  minWidth: 82,
  maxWidth: 82,
  display: "inline-flex",
};

const tireCompactNumberTextFieldSx = {
  ...cellTextFieldSx,
  width: "100%",
  minWidth: 0,
  display: "inline-flex",
};

const currencyStartAdornment = (
  <InputAdornment position="start" sx={{ mr: 0.25 }}>
    $
  </InputAdornment>
);

const oilGridSx = {
  border: "1px solid #E5E7EB",
  borderRadius: 1,
  overflow: "hidden",
  width: "100%",
};

const oilPrimaryRowSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr) 130px",
  columnGap: 1.5,
  rowGap: 1,
  "@media (max-width: 720px)": {
    gridTemplateColumns: "1fr",
  },
};

const oilSecondaryRowSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr) 130px",
  columnGap: 1.5,
  rowGap: 1,
  "@media (max-width: 720px)": {
    gridTemplateColumns: "1fr",
  },
};

const oilGridCellSx = {
  minWidth: 0,
  p: 1,
  minHeight: 82,
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  justifyContent: "flex-start",
};

const oilFieldLabelSx = {
  fontSize: 11.5,
  fontWeight: 800,
  lineHeight: 1.1,
  color: "#2F5597",
  textTransform: "uppercase",
};

const oilFieldValueSx = {
  fontSize: 13,
  fontWeight: 500,
  color: "#1F2937",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const oilInlineFieldsSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  gap: 1,
  alignItems: "end",
};

const extraOilInlineFieldsSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto auto",
  gap: 1,
  alignItems: "end",
};

const tireGridSx = {
  ...oilGridSx,
  "& .oil-grid-cell": {
    p: 0.75,
    minHeight: 76,
  },
};

const tireRowSx = {
  display: "grid",
  gridTemplateColumns:
    "80px 90px minmax(120px, 1fr) 82px 54px 65px minmax(130px, 1fr) 125px",
  columnGap: 0.25,
  rowGap: 0.5,
  "@media (max-width: 720px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  "@media (max-width: 480px)": {
    gridTemplateColumns: "1fr",
  },
};

const tireConditionTextFieldSx = {
  ...cellTextFieldSx,
  width: "100%",
  minWidth: 0,
  display: "inline-flex",
};

const tireAvailableTextFieldSx = {
  ...cellTextFieldSx,
  width: "100%",
  minWidth: 0,
  display: "inline-flex",
};

const PART_COLUMNS = [
  { key: "partNumber", header: "Part #", width: 82, kind: "text" },
  { key: "vendor", header: "Vendor", width: 160, kind: "vendorSelect" },
  {
    key: "buyPrice",
    header: "Buy $",
    width: 68,
    align: "right",
    kind: "number",
    step: 0.01,
    min: 0,
  },
  {
    key: "price",
    header: "Sell $",
    width: 68,
    align: "right",
    kind: "number",
    step: 0.01,
    min: 0,
    isPricingField: true,
  },
  {
    key: "quantity",
    header: "Qty",
    width: 52,
    align: "right",
    kind: "number",
    min: 0,
    isPricingField: true,
  },
  { key: "invoiceNo", header: "Invoice #", width: 92, kind: "text" },
  { key: "invoiceDate", header: "Invoice Date", width: 108, kind: "date" },
];

const partSpreadsheetTableSx = {
  minWidth: 940,
  tableLayout: "fixed",
  borderCollapse: "separate",
  borderSpacing: 0,
  "& th, & td": {
    height: 42,
    px: 0.75,
    py: 0.5,
    borderRight: "1px solid #E5E7EB",
    borderBottom: "1px solid #E5E7EB",
    verticalAlign: "middle",
  },
  "& th": {
    position: "sticky",
    top: 0,
    zIndex: 1,
    bgcolor: "#F8FAFC",
    color: "#2F5597",
    fontSize: 11.5,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0,
  },
  "& th:last-of-type, & td:last-of-type": {
    borderRight: 0,
  },
  "& tbody tr:hover td": {
    bgcolor: "#F8FAFC",
  },
  "& tbody tr:last-of-type td": {
    borderBottom: 0,
  },
};

const partSpreadsheetEditRowSx = {
  "& td": {
    bgcolor: "#FFFFFF",
  },
};

const partSpreadsheetCellTextSx = {
  fontSize: 12.5,
  lineHeight: 1.25,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const partSpreadsheetInputSx = {
  ...cellTextFieldSx,
  "& .MuiInputBase-root": {
    ...cellTextFieldSx["& .MuiInputBase-root"],
    bgcolor: "transparent",
    color: "#111827",
    fontWeight: 500,
  },
  "& .MuiInputBase-input": {
    ...cellTextFieldSx["& .MuiInputBase-input"],
    color: "#111827",
    fontWeight: 500,
    WebkitTextFillColor: "#111827",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#6B7280",
    opacity: 1,
    WebkitTextFillColor: "#6B7280",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: 0,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    border: 0,
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    bgcolor: "transparent",
    boxShadow: "none",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: 0,
  },
};

function formatPartColumnValue(col, value) {
  const raw = value[col.key];
  if (col.condition && !col.condition(value)) return "—";
  if (col.kind === "select") {
    const found = col.options.find((o) => o.value === raw);
    return found ? found.label : raw || "—";
  }
  if (col.kind === "vendorSelect") return raw || "—";
  if (col.kind === "number") {
    return raw === "" || raw === null || typeof raw === "undefined"
      ? "—"
      : col.align === "right" && col.key !== "quantity"
        ? `$${Number(raw).toFixed(2)}`
        : String(raw);
  }
  if (col.kind === "date") return raw ? toPartDateInput(raw) : "—";
  return raw || "—";
}

function renderPartColumnEditor(
  col,
  value,
  key,
  onUpdateLine,
  commitPriceQty,
  vendorOptions,
) {
  const disabled = col.condition && !col.condition(value);

  if (col.key === "price" && value.groupServicePriceOverride) {
    return (
      <Tooltip title="Set by Group Service">
        <Typography
          component="span"
          sx={{
            ...partSpreadsheetCellTextSx,
            display: "block",
            textAlign: "right",
            color: "#6D28D9",
            fontWeight: 700,
          }}
        >
          ${Number(value.price || 0).toFixed(2)}
        </Typography>
      </Tooltip>
    );
  }

  if (col.kind === "vendorSelect") {
    return (
      <TextField
        select
        size="small"
        fullWidth
        value={value.vendor || ""}
        sx={partSpreadsheetInputSx}
        onChange={(e) => onUpdateLine(key, { vendor: e.target.value })}
      >
        <MenuItem value="">—</MenuItem>
        {vendorOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (col.kind === "number") {
    return (
      <TextField
        type="number"
        size="small"
        fullWidth
        value={value[col.key] ?? ""}
        disabled={disabled}
        inputProps={{
          min: col.min ?? 0,
          step: col.step || 1,
          style: { textAlign: "right" },
        }}
        sx={partSpreadsheetInputSx}
        onChange={(e) => {
          const raw = e.target.value;
          const nextVal = raw === "" ? "" : Number(raw);
          if (col.isPricingField) {
            commitPriceQty({
              [col.key]:
                raw === "" ? (col.key === "quantity" ? "" : 0) : nextVal,
            });
          } else {
            onUpdateLine(key, { [col.key]: nextVal });
          }
        }}
      />
    );
  }

  if (col.kind === "date") {
    return (
      <TextField
        type="date"
        size="small"
        fullWidth
        value={toPartDateInput(value.invoiceDate)}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        sx={partSpreadsheetInputSx}
        onChange={(e) => onUpdateLine(key, { invoiceDate: e.target.value })}
      />
    );
  }

  return (
    <TextField
      size="small"
      fullWidth
      value={value[col.key] || ""}
      disabled={disabled}
      sx={partSpreadsheetInputSx}
      onChange={(e) => onUpdateLine(key, { [col.key]: e.target.value })}
    />
  );
}

function PartRow({
  row,
  isEditing,
  onActivate,
  onDone,
  onUpdateLine,
  onRemoveLine,
  vendorOptions,
}) {
  const value = row.value || {};
  const key = value.lineItemId;
  const stopRowClick = (e) => e.stopPropagation();
  const isSpecialOrder = value.partType === "Non Inventory";
  const isGroupServicePart = Boolean(
    value.relatedServiceLineId && value.includedInGroupService,
  );
  const partTypeLabel = isGroupServicePart
    ? "GROUP"
    : isSpecialOrder
      ? "SO"
      : "INV";
  const partTypeBadgeSx = {
    flexShrink: 0,
    px: 0.65,
    py: 0.2,
    borderRadius: 1,
    fontSize: 10.5,
    fontWeight: 700,
    lineHeight: 1,
    color: isGroupServicePart
      ? "#6D28D9"
      : isSpecialOrder
        ? "#475569"
        : "#166534",
    bgcolor: isGroupServicePart
      ? "#EDE9FE"
      : isSpecialOrder
        ? "#E2E8F0"
        : "#DCFCE7",
  };

  const commitPriceQty = (patch) => {
    const price = Number(patch.price ?? value.price) || 0;
    const quantity = Number(patch.quantity ?? value.quantity) || 0;
    onUpdateLine(key, { ...patch, total_price: price * quantity });
  };

  if (!isEditing) {
    return (
      <TableRow
        hover
        onClick={onActivate}
        sx={{ cursor: "pointer", "&:hover .row-edit-hint": { opacity: 1 } }}
      >
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              fontWeight={600}
              sx={partSpreadsheetCellTextSx}
              color={value.name ? "text.primary" : "text.disabled"}
            >
              {value.name || "Untitled part — click to edit"}
            </Typography>
            <Box component="span" sx={partTypeBadgeSx}>
              {partTypeLabel}
            </Box>
            <EditOutlinedIcon
              className="row-edit-hint"
              sx={{
                fontSize: 14,
                opacity: 0,
                color: "text.disabled",
                transition: "opacity .15s",
              }}
            />
          </Box>
        </TableCell>
        {PART_COLUMNS.map((col) => (
          <TableCell key={col.key} align={col.align || "left"}>
            <Typography
              sx={partSpreadsheetCellTextSx}
              color={
                formatPartColumnValue(col, value) === "—"
                  ? "text.disabled"
                  : "text.primary"
              }
            >
              {formatPartColumnValue(col, value)}
            </Typography>
          </TableCell>
        ))}
        <TableCell align="right">
          <Typography
            fontWeight={700}
            sx={partSpreadsheetCellTextSx}
            color={value.includedInGroupService ? "#6D28D9" : "text.primary"}
          >
            {value.includedInGroupService
              ? "Included"
              : `$${Number(value.total_price || 0).toFixed(2)}`}
          </Typography>
        </TableCell>
        <TableCell align="right" onClick={stopRowClick}>
          <Tooltip title="Remove">
            <IconButton
              size="small"
              color="error"
              onClick={() => onRemoveLine(key)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow sx={partSpreadsheetEditRowSx}>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Part name"
            value={value.name || ""}
            sx={{ ...partSpreadsheetInputSx, minWidth: 0 }}
            onChange={(e) => onUpdateLine(key, { name: e.target.value })}
          />
          <Box component="span" sx={partTypeBadgeSx}>
            {partTypeLabel}
          </Box>
        </Box>
      </TableCell>
      {PART_COLUMNS.map((col) => (
        <TableCell
          key={col.key}
          align={col.align || "left"}
        >
          {renderPartColumnEditor(
            col,
            value,
            key,
            onUpdateLine,
            commitPriceQty,
            vendorOptions,
          )}
        </TableCell>
      ))}
      <TableCell align="right">
        <Typography
          fontWeight={700}
          fontSize={13}
          color={value.includedInGroupService ? "#6D28D9" : "text.primary"}
        >
          {value.includedInGroupService
            ? "Included"
            : `$${Number(value.total_price || 0).toFixed(2)}`}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
          <Tooltip title="Remove">
            <IconButton
              size="small"
              color="error"
              onClick={() => onRemoveLine(key)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Done">
            <IconButton size="small" sx={{ color: "#7c3aed" }} onClick={onDone}>
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function ServiceRow({
  row,
  isEditing,
  onActivate,
  onDone,
  onUpdateLine,
  onRemoveLine,
  techniciansOptions,
  validationErrors = {},
  TOKEN,
}) {
  const value = row.value || {};
  const key = value.lineItemId;
  const isGroup = value.serviceType === "Group";
  const isCustom = !!value.isCustom;
  const technicians = Array.isArray(value.technicians)
    ? value.technicians
    : value.technician
      ? [value.technician]
      : [];
  const missingTechnician = technicians.length === 0;

  const stopRowClick = (e) => e.stopPropagation();
  const fieldError = (name) => validationErrors?.[name];

  const commitPriceQty = (patch) => {
    const price = Number(patch.price ?? value.price) || 0;
    const quantity = Number(patch.quantity ?? value.quantity) || 0;
    onUpdateLine(key, { ...patch, total_price: price * quantity });
  };

  if (!isEditing) {
    return (
      <TableRow
        hover
        onClick={onActivate}
        sx={{ cursor: "pointer", "&:hover .row-edit-hint": { opacity: 1 } }}
      >
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              fontWeight={600}
              fontSize={13}
              color={value.name ? "text.primary" : "text.disabled"}
            >
              {value.name || "Untitled service — click to edit"}
            </Typography>
            {(isGroup || isCustom) && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  px: 0.65,
                  py: 0.2,
                  borderRadius: 1,
                  fontSize: 10.5,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: isGroup ? "#6D28D9" : "#475569",
                  bgcolor: isGroup ? "#EDE9FE" : "#E2E8F0",
                }}
              >
                {isGroup ? "Group" : "Custom"}
              </Box>
            )}
            <EditOutlinedIcon
              className="row-edit-hint"
              sx={{
                fontSize: 14,
                opacity: 0,
                color: "text.disabled",
                transition: "opacity .15s",
              }}
            />
          </Box>
        </TableCell>

        <TableCell>
          <Typography
            fontSize={12.5}
            color={value.description ? "text.secondary" : "text.disabled"}
          >
            {value.description || "—"}
          </Typography>
        </TableCell>

        <TableCell>
          {technicians.length > 0 ? (
            <Typography fontSize={12.5}>
              {technicians.map((t) => t.label || t.name).join(", ")}
            </Typography>
          ) : (
            <Typography fontSize={12.5} sx={{ color: "#DC2626" }}>
              No technician
            </Typography>
          )}
        </TableCell>

        <TableCell align="right">
          <Typography fontSize={13}>
            ${Number(value.price || 0).toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography fontSize={13}>{value.quantity ?? 0}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography fontWeight={700} fontSize={13}>
            ${Number(value.total_price || 0).toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell align="right" onClick={stopRowClick}>
          <Tooltip title="Remove">
            <IconButton
              size="small"
              color="error"
              onClick={() => onRemoveLine(key)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow sx={{ bgcolor: `${TOKEN.primary}0A` }}>
      <TableCell sx={{ verticalAlign: "top", py: 1 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder={isGroup ? "Group service name" : "Service name"}
          value={value.name || ""}
          error={Boolean(fieldError("serviceName"))}
          helperText={fieldError("serviceName") || ""}
          sx={cellTextFieldSx}
          onChange={(e) => onUpdateLine(key, { name: e.target.value })}
        />
      </TableCell>

      <TableCell sx={{ verticalAlign: "top", py: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Description (optional)"
          value={value.description || ""}
          sx={cellTextFieldSx}
          onChange={(e) => onUpdateLine(key, { description: e.target.value })}
        />
      </TableCell>

      <TableCell sx={{ verticalAlign: "top", py: 1 }}>
        <Select
          isMulti
          options={techniciansOptions}
          value={technicians}
          onChange={(list) => onUpdateLine(key, { technicians: list || [] })}
          styles={{
            ...inlineSelectStyles,
            control: (base, state) => ({
              ...inlineSelectStyles.control(base, state),
              borderColor: missingTechnician ? "#DC2626" : base.borderColor,
            }),
          }}
          menuPortalTarget={menuPortalTarget}
          menuPosition="fixed"
          placeholder="Select…"
        />
        {fieldError("technician") && (
          <Typography sx={{ color: "#DC2626", fontSize: 11, mt: 0.4 }}>
            {fieldError("technician")}
          </Typography>
        )}
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: "top", py: 1 }}>
        <TextField
          type="number"
          size="small"
          value={value.price ?? ""}
          inputProps={{ min: 0, step: 0.01, style: { textAlign: "right" } }}
          sx={{ ...cellTextFieldSx, width: 88 }}
          onChange={(e) =>
            commitPriceQty({
              price: e.target.value === "" ? 0 : Number(e.target.value),
            })
          }
        />
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: "top", py: 1 }}>
        <TextField
          type="number"
          size="small"
          value={value.quantity ?? ""}
          error={Boolean(fieldError("serviceQuantity"))}
          helperText={fieldError("serviceQuantity") || ""}
          inputProps={{ min: 1, style: { textAlign: "right" } }}
          sx={{ ...cellTextFieldSx, width: 68 }}
          onChange={(e) =>
            commitPriceQty({
              quantity: e.target.value === "" ? "" : Number(e.target.value),
            })
          }
        />
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: "top", py: 1.5 }}>
        <Typography fontWeight={700} fontSize={13}>
          ${Number(value.total_price || 0).toFixed(2)}
        </Typography>
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: "top", py: 1 }}>
        <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
          <Tooltip title="Remove">
            <IconButton
              size="small"
              color="error"
              onClick={() => onRemoveLine(key)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Done">
            <IconButton size="small" sx={{ color: TOKEN.primary }} onClick={onDone}>
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function TireRow({
  tire,
  isEditing,
  onActivate,
  onDone,
  onUpdateTireLine,
  onRemoveTireLine,
  techniciansOptions,
  TOKEN,
}) {
  const id = tire.id;
  const stopRowClick = (e) => e.stopPropagation();
  const available = Number(tire.available || 0);
  const missingTechnician = !tire.technician;
  const canEditTireIdentity = Boolean(tire.isBlank);

  const commitPriceQty = (patch) => {
    let qty = Number(patch.quantity ?? tire.quantity) || 0;
    if (available > 0) qty = Math.min(qty, available);
    const price = Number(patch.price ?? tire.price) || 0;
    onUpdateTireLine(id, { ...patch, quantity: qty, total_price: price * qty });
  };

  if (!isEditing) {
    return (
      <Box
        onClick={onActivate}
        sx={{
          ...tireGridSx,
          cursor: "pointer",
          "&:hover .row-edit-hint": { opacity: 1 },
        }}
      >
        <Box sx={tireRowSx}>
          <OilGridCell label="Type">
            <Typography sx={oilFieldValueSx}>{tire.condition || "New"}</Typography>
          </OilGridCell>
          <OilGridCell label="Size">
            <Typography sx={oilFieldValueSx}>
              {formatTireSizeInput(tire.size) || "-"}
            </Typography>
          </OilGridCell>
          <OilGridCell label="Brand">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <Typography sx={oilFieldValueSx}>
                {tire.brand || "Untitled - click to edit"}
              </Typography>
              <EditOutlinedIcon
                className="row-edit-hint"
                sx={{
                  fontSize: 14,
                  opacity: 0,
                  color: "text.disabled",
                  transition: "opacity .15s",
                  flexShrink: 0,
                }}
              />
            </Box>
          </OilGridCell>
          <OilGridCell label="Price">
            <Typography sx={oilFieldValueSx}>
              ${Number(tire.price || 0).toFixed(2)}
            </Typography>
          </OilGridCell>
          <OilGridCell label="Qty">
            <Typography sx={oilFieldValueSx}>{tire.quantity ?? 0}</Typography>
          </OilGridCell>
          <OilGridCell label="Avail">
            <Typography sx={oilFieldValueSx}>
              {tire.isBlank ? "-" : available}
            </Typography>
          </OilGridCell>
          <OilGridCell label="Technician">
            <Typography
              sx={{
                ...oilFieldValueSx,
                color: missingTechnician ? "#DC2626" : oilFieldValueSx.color,
              }}
            >
              {tire.technician
                ? tire.technician.label || tire.technician.name
                : "No technician"}
            </Typography>
          </OilGridCell>
          <OilGridCell label="Total">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} fontSize={13}>
                  ${Number(tire.total_price || 0).toFixed(2)}
                </Typography>
              </Box>
              <Box onClick={stopRowClick}>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemoveTireLine(id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </OilGridCell>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ ...tireGridSx, bgcolor: `${TOKEN.primary}0A` }}>
      <Box sx={tireRowSx}>
        <OilGridCell label="Type">
          {canEditTireIdentity ? (
            <TextField
              select
              size="small"
              value={tire.condition || "New"}
              sx={tireConditionTextFieldSx}
              onChange={(e) =>
                onUpdateTireLine(id, { condition: e.target.value })
              }
            >
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Used">Used</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={tire.condition || "New"}
              InputProps={{ readOnly: true }}
              sx={tireConditionTextFieldSx}
            />
          )}
        </OilGridCell>
        <OilGridCell label="Size">
          {canEditTireIdentity ? (
            <TextField
              size="small"
              fullWidth
              placeholder="Size"
              value={tire.size || ""}
              inputProps={{ maxLength: 9, inputMode: "numeric" }}
              sx={cellTextFieldSx}
              onChange={(e) =>
                onUpdateTireLine(id, {
                  size: formatTireSizeInput(e.target.value),
                })
              }
              onBlur={() =>
                onUpdateTireLine(id, {
                  size: formatTireSizeInput(tire.size),
                })
              }
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              value={formatTireSizeInput(tire.size) || "-"}
              InputProps={{ readOnly: true }}
              sx={cellTextFieldSx}
            />
          )}
        </OilGridCell>
        <OilGridCell label="Brand">
          {canEditTireIdentity ? (
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Brand"
              value={tire.brand || ""}
              sx={cellTextFieldSx}
              onChange={(e) => onUpdateTireLine(id, { brand: e.target.value })}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              value={tire.brand || "Untitled"}
              InputProps={{ readOnly: true }}
              sx={cellTextFieldSx}
            />
          )}
        </OilGridCell>
        <OilGridCell label="Price">
          <TextField
            type="number"
            size="small"
            autoFocus={!canEditTireIdentity}
            value={tire.price ?? ""}
            InputProps={{ startAdornment: currencyStartAdornment }}
            inputProps={{
              min: 0,
              step: 0.01,
              style: { textAlign: "right" },
            }}
            sx={tireCompactNumberTextFieldSx}
            onChange={(e) =>
              commitPriceQty({
                price: e.target.value === "" ? 0 : Number(e.target.value),
              })
            }
          />
        </OilGridCell>
        <OilGridCell label="Qty">
          <TextField
            type="number"
            size="small"
            value={tire.quantity ?? ""}
            inputProps={{
              min: 1,
              max: available || undefined,
              style: { textAlign: "right" },
            }}
            sx={tireCompactNumberTextFieldSx}
            onChange={(e) =>
              commitPriceQty({
                quantity: e.target.value === "" ? "" : Number(e.target.value),
              })
            }
          />
        </OilGridCell>
        <OilGridCell label="Avail">
          <TextField
            size="small"
            value={tire.isBlank ? "-" : available}
            InputProps={{ readOnly: true }}
            sx={tireAvailableTextFieldSx}
          />
        </OilGridCell>
        <OilGridCell label="Technician">
          <Select
            options={techniciansOptions}
            value={tire.technician || null}
            isClearable
            onChange={(tech) =>
              onUpdateTireLine(id, { technician: tech || null })
            }
            styles={{
              ...inlineSelectStyles,
              control: (base, state) => ({
                ...inlineSelectStyles.control(base, state),
                borderColor: missingTechnician ? "#DC2626" : base.borderColor,
              }),
            }}
            menuPortalTarget={menuPortalTarget}
            menuPosition="fixed"
            placeholder="Select..."
          />
        </OilGridCell>
        <OilGridCell label="Total">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={oilFieldValueSx}>
                ${Number(tire.total_price || 0).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemoveTireLine(id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Done">
                <IconButton
                  size="small"
                  sx={{ color: TOKEN.primary }}
                  onClick={onDone}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </OilGridCell>
      </Box>
    </Box>
  );
}

function OilChangeTable({
  TOKEN,
  oilChangeAdded,
  isEditing,
  onActivate,
  onDone,
  oilsOptions,
  filtersOptions,
  extraOilOptions,
  techniciansOptions,
  selectedOil,
  selectedExtraOil,
  selectedOilFilter,
  oilTechnician,
  receiptOil,
  FULL_SERVICE_PRICE,
  onSelectOil,
  onOilPriceOverride,
  onSelectExtraOil,
  onExtraOilQtyChange,
  onExtraOilUnitPriceChange,
  onSelectOilFilter,
  onOilFilterPriceOverride,
  onToggleFullService,
  onSelectOilTechnician,
  onAddOilChangeRow,
  onRemoveOilChange,
}) {
  const oilPrice = Number(receiptOil.oilPrice || 0);
  const extraOilPrice = Number(receiptOil.extraOilPrice || 0);
  const oilFilterPrice = Number(receiptOil.oilFilterPrice || 0);
  const fullServiceDisplayPrice = Number(
    receiptOil.fullServicePrice || FULL_SERVICE_PRICE || 0,
  );
  const fullServicePrice = receiptOil.fullServiceIncluded
    ? fullServiceDisplayPrice
    : 0;
  const grandTotal =
    oilPrice + extraOilPrice + oilFilterPrice + fullServicePrice;
  const missingTechnician = !oilTechnician || oilTechnician.length === 0;
  const stopRowClick = (e) => e.stopPropagation();
  const oilName = receiptOil.oil
    ? `${receiptOil.oil.brand || ""} ${receiptOil.oil.name || ""}`.trim()
    : "Not selected - click to edit";
  const extraOilName = receiptOil.extraOilSelection
    ? `${receiptOil.extraOilSelection.brand || ""} ${receiptOil.extraOilSelection.name || ""}`.trim()
    : "None";
  const oilFilterName = receiptOil.oilFilter
    ? `${receiptOil.oilFilter.brand || ""} ${receiptOil.oilFilter.name || receiptOil.oilFilter.type || ""}`.trim()
    : "Not selected";
  const technicianNames =
    oilTechnician?.length > 0
      ? oilTechnician.map((t) => t.label || t.name).join(", ")
      : "No technician";

  /* ── Empty state: no oil-change row added yet ── */
  if (!oilChangeAdded) {
    return <AddOilChangeRow TOKEN={TOKEN} onAdd={onAddOilChangeRow} />;
  }

  if (!isEditing) {
    return (
      <Box
        onClick={onActivate}
        sx={{
          ...oilGridSx,
          cursor: "pointer",
          "&:hover .row-edit-hint": { opacity: 1 },
        }}
      >
        <Box sx={oilPrimaryRowSx}>
          <OilGridCell label="Oil Type">
            <Box sx={oilInlineFieldsSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                <Typography
                  sx={oilFieldValueSx}
                >
                  {oilName}
                </Typography>
                <EditOutlinedIcon
                  className="row-edit-hint"
                  sx={{
                    fontSize: 14,
                    opacity: 0,
                    color: "text.disabled",
                    transition: "opacity .15s",
                    flexShrink: 0,
                  }}
                />
              </Box>
              <Typography sx={{ ...oilFieldValueSx, textAlign: "right" }}>
                ${oilPrice.toFixed(2)}
              </Typography>
            </Box>
          </OilGridCell>
          <OilGridCell label="Extra Oil">
            <Box sx={extraOilInlineFieldsSx}>
              <Typography sx={oilFieldValueSx}>
                {extraOilName}
              </Typography>
              <Typography sx={{ ...oilFieldValueSx, textAlign: "right" }}>
                Qty {receiptOil.extraOilQuantity || 0}
              </Typography>
              <Typography sx={{ ...oilFieldValueSx, textAlign: "right" }}>
                ${Number(receiptOil.extraOilUnitPrice || 0).toFixed(2)}/qt
              </Typography>
            </Box>
          </OilGridCell>
          <OilGridCell label="Full Service" sx={{ borderRight: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={oilFieldValueSx}>
                {receiptOil.fullServiceIncluded ? "Yes" : "No"}
              </Typography>
              <Typography sx={oilFieldValueSx}>
                ${fullServiceDisplayPrice.toFixed(2)}
              </Typography>
            </Box>
          </OilGridCell>
        </Box>
        <Box sx={oilSecondaryRowSx}>
          <OilGridCell label="Oil Filter">
            <Box sx={oilInlineFieldsSx}>
              <Typography sx={oilFieldValueSx}>
                {oilFilterName}
              </Typography>
              <Typography sx={{ ...oilFieldValueSx, textAlign: "right" }}>
                ${oilFilterPrice.toFixed(2)}
              </Typography>
            </Box>
          </OilGridCell>
          <OilGridCell label="Technician">
            <Typography
              sx={{
                ...oilFieldValueSx,
                color: missingTechnician ? "#DC2626" : oilFieldValueSx.color,
              }}
            >
              {technicianNames}
            </Typography>
          </OilGridCell>
          <OilGridCell label="Total" sx={{ borderRight: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography fontWeight={700} fontSize={13}>
                ${grandTotal.toFixed(2)}
              </Typography>
              <Box onClick={stopRowClick}>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemoveOilChange && onRemoveOilChange()}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </OilGridCell>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ ...oilGridSx, bgcolor: `${TOKEN.primary}0A` }}>
      <Box sx={oilPrimaryRowSx}>
        <OilGridCell label="Oil Type">
          <Box sx={oilInlineFieldsSx}>
            <Select
              options={oilsOptions}
              isClearable
              value={selectedOil}
              onChange={onSelectOil}
              placeholder="Choose an oil"
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              styles={wideInlineSelectMenuStyles}
            />
            <TextField
              type="number"
              size="small"
              value={receiptOil.oilPrice ?? 0}
              disabled={!receiptOil.oil}
              InputProps={{ startAdornment: currencyStartAdornment }}
              inputProps={{
                min: 0,
                step: 0.01,
                style: { textAlign: "right" },
              }}
              sx={oilCompactNumberTextFieldSx}
              onChange={(e) => onOilPriceOverride(e.target.value)}
            />
          </Box>
        </OilGridCell>
        <OilGridCell label="Extra Oil">
          <Box sx={extraOilInlineFieldsSx}>
            <Select
              options={extraOilOptions}
              isClearable
              value={selectedExtraOil}
              onChange={onSelectExtraOil}
              placeholder="Choose extra oil"
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              styles={wideInlineSelectMenuStyles}
            />
            <TextField
              type="number"
              size="small"
              value={receiptOil.extraOilQuantity ?? 0}
              inputProps={{ min: 0, style: { textAlign: "right" } }}
              sx={oilCompactNumberTextFieldSx}
              onChange={(e) => onExtraOilQtyChange(e.target.value)}
            />
            <TextField
              type="number"
              size="small"
              value={receiptOil.extraOilUnitPrice ?? 0}
              disabled={!receiptOil.extraOilSelection}
              InputProps={{ startAdornment: currencyStartAdornment }}
              inputProps={{
                min: 0,
                step: 0.01,
                style: { textAlign: "right" },
              }}
              sx={oilCompactNumberTextFieldSx}
              onChange={(e) => onExtraOilUnitPriceChange(e.target.value)}
            />
          </Box>
        </OilGridCell>
        <OilGridCell label="Full Service" sx={{ borderRight: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              size="small"
              checked={!!receiptOil.fullServiceIncluded}
              onChange={(e) => onToggleFullService(e.target.checked)}
              sx={{
                p: 0,
                color: TOKEN.primary,
                "&.Mui-checked": { color: TOKEN.primary },
              }}
            />
            <Typography sx={oilFieldValueSx}>
              ${fullServiceDisplayPrice.toFixed(2)}
            </Typography>
          </Box>
        </OilGridCell>
      </Box>
      <Box sx={oilSecondaryRowSx}>
        <OilGridCell label="Oil Filter">
          <Box sx={oilInlineFieldsSx}>
            <Select
              options={filtersOptions}
              isClearable
              value={selectedOilFilter}
              onChange={onSelectOilFilter}
              placeholder="Choose a filter"
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              styles={wideInlineSelectMenuStyles}
            />
            <TextField
              type="number"
              size="small"
              value={receiptOil.oilFilterPrice ?? 0}
              disabled={!receiptOil.oilFilter}
              InputProps={{ startAdornment: currencyStartAdornment }}
              inputProps={{
                min: 0,
                step: 0.01,
                style: { textAlign: "right" },
              }}
              sx={oilCompactNumberTextFieldSx}
              onChange={(e) => onOilFilterPriceOverride(e.target.value)}
            />
          </Box>
        </OilGridCell>
        <OilGridCell label="Technician">
          <Select
            isMulti
            options={techniciansOptions}
            value={oilTechnician}
            onChange={onSelectOilTechnician}
            placeholder="Select Technician"
            menuPortalTarget={menuPortalTarget}
            menuPosition="fixed"
            styles={{
              ...inlineSelectStyles,
              control: (base, state) => ({
                ...inlineSelectStyles.control(base, state),
                borderColor: missingTechnician ? "#DC2626" : base.borderColor,
              }),
              menuPortal: (base) => ({ ...base, zIndex: 1600 }),
            }}
          />
        </OilGridCell>
        <OilGridCell label="Total" sx={{ borderRight: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography fontWeight={700} fontSize={13}>
              ${grandTotal.toFixed(2)}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemoveOilChange && onRemoveOilChange()}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Done">
                <IconButton size="small" sx={{ color: TOKEN.primary }} onClick={onDone}>
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </OilGridCell>
      </Box>
    </Box>
  );
}

export default React.memo(ServicesPartsInlineTables);
